import { NavItemType } from 'types/menu';
import type { IntlShape } from 'react-intl';

export type SentinelRole = 'super_admin' | 'support' | 'tenant_super_admin' | 'tenant_admin' | 'tenant_tech';

export function normalizeRole(userRole: string | undefined): SentinelRole | undefined {
  if (
    userRole === 'super_admin' ||
    userRole === 'support' ||
    userRole === 'tenant_super_admin' ||
    userRole === 'tenant_admin' ||
    userRole === 'tenant_tech'
  ) {
    return userRole;
  }

  return undefined;
}

export function hasRole(userRole: string | undefined, allowedRoles: SentinelRole[]): boolean {
  const normalizedRole = normalizeRole(userRole);
  if (!normalizedRole) {
    return false;
  }

  return allowedRoles.includes(normalizedRole);
}

export function canManageUsers(userRole: string | undefined): boolean {
  return hasRole(userRole, ['super_admin', 'support', 'tenant_super_admin', 'tenant_admin']);
}

export function canReassignDeviceTenant(userRole: string | undefined): boolean {
  return hasRole(userRole, ['super_admin', 'support']);
}

export function canControlUpdateRollouts(userRole: string | undefined): boolean {
  return hasRole(userRole, ['super_admin', 'support', 'tenant_super_admin', 'tenant_admin']);
}

export function canUseRestoreCommand(userRole: string | undefined): boolean {
  return hasRole(userRole, ['super_admin', 'support', 'tenant_super_admin']);
}

export function roleLabel(role: string | undefined): string {
  switch (normalizeRole(role)) {
    case 'super_admin':
      return 'Super Admin';
    case 'support':
      return 'Support';
    case 'tenant_super_admin':
      return 'Tenant Super Admin';
    case 'tenant_admin':
      return 'Tenant Admin';
    case 'tenant_tech':
      return 'Tenant Tech';
    default:
      return 'User';
  }
}

export function roleMessageId(role: string | undefined): string {
  switch (normalizeRole(role)) {
    case 'super_admin':
      return 'sentinel.roles.super_admin';
    case 'support':
      return 'sentinel.roles.support';
    case 'tenant_super_admin':
      return 'sentinel.roles.tenant_super_admin';
    case 'tenant_admin':
      return 'sentinel.roles.tenant_admin';
    case 'tenant_tech':
      return 'sentinel.roles.tenant_tech';
    default:
      return 'sentinel.roles.user';
  }
}

export function formatRoleLabel(intl: IntlShape, role: string | undefined): string {
  return intl.formatMessage({ id: roleMessageId(role), defaultMessage: roleLabel(role) });
}

export function filterNavigationByRole(items: NavItemType[], role: string | undefined): NavItemType[] {
  const hiddenIds = new Set<string>();

  if (role !== 'super_admin') {
    hiddenIds.add('group-live-report');
    hiddenIds.add('commands');
    hiddenIds.add('request-types');
    hiddenIds.add('device-models');
    hiddenIds.add('crashes');
  }

  if (!hasRole(role, ['super_admin', 'support'])) {
    hiddenIds.add('group-diagnostics');
    hiddenIds.add('command-rejections');
  }

  if (role !== 'super_admin') {
    hiddenIds.add('customers');
  }

  if (!hasRole(role, ['super_admin', 'support', 'tenant_super_admin', 'tenant_admin'])) {
    hiddenIds.add('users');
  }

  if (!hasRole(role, ['super_admin', 'support', 'tenant_super_admin'])) {
    hiddenIds.add('update-approvals');
  }

  if (!hasRole(role, ['super_admin', 'support', 'tenant_super_admin', 'tenant_admin'])) {
    hiddenIds.add('knowledge-categories');
  }

  const filterItem = (item: NavItemType): NavItemType | null => {
    if (item.id && hiddenIds.has(item.id)) {
      return null;
    }

    if (item.children) {
      const filteredChildren = item.children
        .map((child) => filterItem(child))
        .filter((child): child is NavItemType => child !== null);

      if (filteredChildren.length === 0 && item.type === 'group' && !item.url) {
        return null;
      }

      return {
        ...item,
        children: filteredChildren
      };
    }

    return item;
  };

  return items.map((item) => filterItem(item)).filter((item): item is NavItemType => item !== null);
}
